import { useEffect } from "react";

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  noindex?: boolean;
  schemas?: object[];
}

function updateMetaTag(
  name: string,
  content: string | undefined,
  attribute: "name" | "property" = "name"
) {
  if (!content) return;
  let meta = document.querySelector(`meta[${attribute}="${name}"]`);
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute(attribute, name);
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", content);
}

function removeMetaTag(
  name: string,
  attribute: "name" | "property" = "name"
) {
  const meta = document.querySelector(`meta[${attribute}="${name}"]`);
  if (meta) meta.remove();
}

function updateCanonical(href: string | undefined) {
  if (!href) return;
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

function injectSchemas(schemas: object[] | undefined) {
  document
    .querySelectorAll('script[type="application/ld+json"]')
    .forEach((el) => el.remove());

  if (!schemas) return;

  schemas.forEach((schema) => {
    const script = document.createElement("script");
    script.setAttribute("type", "application/ld+json");
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
  });
}

export default function SEO({
  title,
  description,
  keywords,
  canonical,
  ogImage = "https://ventracore.com.mx/og-image.jpg",
  ogType = "website",
  noindex = false,
  schemas,
}: SEOProps) {
  useEffect(() => {
    document.title = title;

    updateMetaTag("description", description);

    if (keywords) updateMetaTag("keywords", keywords);
    else removeMetaTag("keywords");

    if (canonical) updateCanonical(canonical);

    updateMetaTag("og:title", title, "property");
    updateMetaTag("og:description", description, "property");
    updateMetaTag("og:url", canonical, "property");
    updateMetaTag("og:type", ogType, "property");
    updateMetaTag("og:image", ogImage, "property");
    updateMetaTag("og:locale", "es_MX", "property");

    updateMetaTag("twitter:title", title);
    updateMetaTag("twitter:description", description);
    updateMetaTag("twitter:image", ogImage);
    updateMetaTag("twitter:card", "summary_large_image");

    if (noindex) {
      updateMetaTag("robots", "noindex, nofollow");
    } else {
      removeMetaTag("robots");
    }

    injectSchemas(schemas);
  }, [
    title,
    description,
    keywords,
    canonical,
    ogImage,
    ogType,
    noindex,
    schemas,
  ]);

  return null;
}